import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from '@mui/material'
import { AlertCircle, AlertTriangle, ArrowLeft, RefreshCw, X } from 'lucide-react'
import Button from '../../ui/Button'
import Input from '../../ui/Input'
import { showConfirmModal, showErrorModal, showSuccessModal } from '../../ui/ConfirmDialog'
import { UndoToast } from '../UndoToast'
import {
  createCodeIndexAppendix,
  fetchCodeIndexAppendix,
  fetchCodeIndexAppendices,
  updateCodeIndexAppendix,
} from '../../../services/appendixService'
import type { AppendixCodeItemOption, AppendixDetail, AppendixSummary } from '../../../types/codeIndex'
import { useAppendixTableDraft } from '../../../hooks/useAppendixTableDraft'
import {
  AppendixColumnDraft,
  AppendixRowDraft,
  SavedAppendixTableMeta,
  buildAppendixPayload,
  formatCount,
  hydrateAppendixDraft,
} from '../../../utils/appendixTable'
import { getApiMessage } from '../../../utils/apiMessage'
import AppendixColumnsSection from './AppendixColumnsSection'
import AppendixRowsSection from './AppendixRowsSection'
import ColumnTypeChangeDialog from './ColumnTypeChangeDialog'

export const DUPLICATE_APPENDIX_NAME_ERROR = 'An appendix on this codesheet already uses this name.'

const FIELD_LABEL_CLASS = 'mb-1 block text-sm font-semibold text-gray-900'

type AppendixEditorModalProps = {
  open: boolean
  codeIndexId: string
  projectId?: string
  codeItems: AppendixCodeItemOption[]
  // Opens an existing appendix; without it the modal creates a new one.
  appendixId?: string
  readOnly?: boolean
  saveDisabledReason?: string
  // Revising a rejected pull request change: the save is allowed while the project is submitted
  // and the caller resubmits the change afterwards (so no "saved" toast here).
  revisionChangeId?: string
  onClose: () => void
  onSaved: (appendix: AppendixSummary) => void
}

type LoadState = 'idle' | 'loading' | 'error' | 'ready'

function serializeDraft(name: string, columns: AppendixColumnDraft[], rows: AppendixRowDraft[]): string {
  return JSON.stringify([name.trim(), columns, rows])
}

function formatAppendixTitle(detail: AppendixDetail): string {
  return detail.letter ? `${detail.letter} — ${detail.title}` : detail.title
}

export const AppendixEditorModal: React.FC<AppendixEditorModalProps> = ({
  open,
  codeIndexId,
  projectId,
  codeItems,
  appendixId,
  readOnly = false,
  saveDisabledReason,
  revisionChangeId,
  onClose,
  onSaved,
}) => {
  const table = useAppendixTableDraft(codeItems)
  const { columns, rows, reset } = table
  const isEditMode = Boolean(appendixId)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [columnsExpanded, setColumnsExpanded] = useState(true)
  const [autoFocusColumnKey, setAutoFocusColumnKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [detail, setDetail] = useState<AppendixDetail | null>(null)
  const [savedMeta, setSavedMeta] = useState<SavedAppendixTableMeta | undefined>(undefined)
  const [baseline, setBaseline] = useState<string | null>(null)
  const nameCheckRef = useRef(0)
  const loadRequestRef = useRef(0)

  const loadAppendix = useCallback(async () => {
    if (!appendixId) return

    const requestId = loadRequestRef.current + 1
    loadRequestRef.current = requestId
    setLoadState('loading')
    setLoadError(null)
    try {
      const loaded = await fetchCodeIndexAppendix(codeIndexId, appendixId, { projectId })
      if (loadRequestRef.current !== requestId) return

      const hydrated = hydrateAppendixDraft(loaded)
      reset({ columns: hydrated.columns, rows: hydrated.rows })
      setDetail(loaded)
      setSavedMeta(hydrated.meta)
      setName(loaded.title)
      setBaseline(serializeDraft(loaded.title, hydrated.columns, hydrated.rows))
      setColumnsExpanded(hydrated.columns.length === 0)
      setLoadState('ready')
    } catch (err) {
      if (loadRequestRef.current !== requestId) return
      console.error('Failed to load appendix.', err)
      setLoadError(getApiMessage(err) || 'Unable to load the appendix.')
      setLoadState('error')
    }
  }, [appendixId, codeIndexId, projectId, reset])

  useEffect(() => {
    loadRequestRef.current += 1
    nameCheckRef.current += 1
    if (!open) return

    reset()
    setName('')
    setNameError(null)
    setColumnsExpanded(true)
    setAutoFocusColumnKey(null)
    setSaving(false)
    setDetail(null)
    setSavedMeta(undefined)
    setBaseline(null)
    setLoadError(null)
    setLoadState('idle')
    if (appendixId) void loadAppendix()
  }, [appendixId, loadAppendix, open, reset])

  const isLoaded = !isEditMode || loadState === 'ready'
  const trimmedName = name.trim()
  const hasTableContent = Boolean(trimmedName && columns.length > 0 && rows.length > 0)
  const isDirty = readOnly || !isLoaded
    ? false
    : isEditMode
      ? serializeDraft(name, columns, rows) !== baseline
      : Boolean(trimmedName || columns.length > 0 || rows.length > 0)
  const canSave = isLoaded && hasTableContent && !nameError && !saving && !readOnly && !saveDisabledReason
  const inputsDisabled = saving || readOnly

  const title = isEditMode ? (detail ? formatAppendixTitle(detail) : 'Appendix') : 'New appendix'
  const footerHint = !isLoaded
    ? ''
    : !hasTableContent
      ? 'Name, a column and a row are needed'
      : `${formatCount(columns.length, 'column')} - ${formatCount(rows.length, 'row')}`

  const handleClose = async () => {
    if (saving) return
    if (isDirty) {
      const confirmed = await showConfirmModal({
        title: isEditMode ? 'Discard changes?' : 'Discard appendix?',
        description: isEditMode ? 'Your changes to this appendix will not be saved.' : 'The appendix you started will not be saved.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
      })
      if (!confirmed) return
    }
    onClose()
  }

  const handleNameChange = (value: string) => {
    setName(value)
    setNameError(null)
    nameCheckRef.current += 1
  }

  const checkNameAvailability = async () => {
    if (!trimmedName || !codeIndexId || readOnly) return

    const requestId = nameCheckRef.current + 1
    nameCheckRef.current = requestId
    try {
      const matches = await fetchCodeIndexAppendices(codeIndexId, { projectId, search: trimmedName })
      if (nameCheckRef.current !== requestId) return
      const normalizedName = trimmedName.toLowerCase()
      const isTaken = matches.some(appendix =>
        appendix.id !== appendixId && appendix.title.trim().toLowerCase() === normalizedName
      )
      setNameError(isTaken ? DUPLICATE_APPENDIX_NAME_ERROR : null)
    } catch (err) {
      // The server checks the name again on save.
      console.error('Failed to check the appendix name.', err)
    }
  }

  const handleAddColumn = () => {
    const columnKey = table.addColumn()
    setAutoFocusColumnKey(columnKey)
    setColumnsExpanded(true)
  }

  const handleSave = async () => {
    if (!canSave) return

    const { columnsValid, cellsValid } = table.validateAll()
    if (!columnsValid) setColumnsExpanded(true)
    if (!columnsValid || !cellsValid) return

    setSaving(true)
    try {
      const payload = buildAppendixPayload(trimmedName, columns, rows, isEditMode ? savedMeta : undefined)
      const summary = appendixId
        ? await updateCodeIndexAppendix(
            codeIndexId,
            appendixId,
            payload,
            revisionChangeId ? { projectId, changeId: revisionChangeId } : { projectId }
          )
        : await createCodeIndexAppendix(codeIndexId, payload, { projectId })
      onSaved(summary)
      if (!revisionChangeId) {
        await showSuccessModal({
          title: isEditMode ? 'Appendix saved' : 'Appendix Created',
          timer: 1200,
          showConfirmButton: false,
        })
      }
    } catch (err) {
      const message = getApiMessage(err)
      if (message === DUPLICATE_APPENDIX_NAME_ERROR) {
        setNameError(message)
        return
      }

      console.error('Failed to save appendix', err)
      await showErrorModal({
        title: 'Error',
        description: message || (isEditMode ? 'Failed to save appendix. Please try again.' : 'Failed to create appendix. Please try again.'),
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const renderBody = () => {
    if (isEditMode && (loadState === 'loading' || loadState === 'idle')) {
      return (
        <div className="flex min-h-[240px] items-center justify-center" role="status" aria-label="Loading appendix">
          <CircularProgress size={24} />
        </div>
      )
    }

    if (isEditMode && loadState === 'error') {
      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 px-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-red-700">Unable to load appendix</p>
            <p className="text-sm text-red-600">{loadError}</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void loadAppendix()}
            leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
          >
            Retry
          </Button>
        </div>
      )
    }

    return (
      <div className="space-y-5">
        <div>
          <label htmlFor="appendix-editor-name" className={FIELD_LABEL_CLASS}>Name</label>
          {nameError ? (
            <p role="alert" className="mb-1 flex items-center gap-1.5 text-xs text-[#B3261E]">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {nameError}
            </p>
          ) : null}
          <Input
            id="appendix-editor-name"
            inputSize="md"
            value={name}
            invalid={Boolean(nameError)}
            placeholder="Give this appendix a name"
            onChange={(event) => handleNameChange(event.target.value)}
            onBlur={() => void checkNameAvailability()}
            disabled={inputsDisabled}
            className={`max-w-[360px]${nameError ? ' !bg-[#FFF5F5]' : ''}`}
          />
        </div>

        <AppendixColumnsSection
          columns={columns}
          codeItems={codeItems}
          columnErrors={table.columnErrors}
          firstColumnError={table.firstColumnError}
          expanded={columnsExpanded}
          autoFocusColumnKey={autoFocusColumnKey}
          disabled={saving}
          readOnly={readOnly}
          onToggleExpanded={() => setColumnsExpanded(current => !current)}
          onAddColumn={handleAddColumn}
          onUpdateColumn={table.updateColumn}
          onRequestTypeChange={table.requestColumnTypeChange}
          onDeleteColumn={table.deleteColumn}
          onMoveColumn={table.moveColumn}
        />

        <AppendixRowsSection
          columns={columns}
          rows={rows}
          codeItems={codeItems}
          cellErrors={table.cellErrors}
          firstCellError={table.firstCellError}
          disabled={saving}
          readOnly={readOnly}
          onAddRow={table.addRow}
          onDeleteRow={table.deleteRow}
          onUpdateCell={table.updateCell}
          onValidateCell={table.validateCellAt}
        />
      </div>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={() => void handleClose()}
      maxWidth={false}
      disableEnforceFocus
      aria-labelledby="appendix-editor-title"
      PaperProps={{ sx: { borderRadius: '8px', width: 780, maxWidth: 'calc(100% - 32px)' } }}
    >
      <div className="flex items-center gap-2 border-b border-[#E2E8F0] px-4 py-3">
        <IconButton size="small" onClick={() => void handleClose()} aria-label="Back to appendices">
          <ArrowLeft className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
        <h2 id="appendix-editor-title" className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900">
          {title}
        </h2>
        <IconButton size="small" onClick={() => void handleClose()} aria-label="Close">
          <X className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: '20px' }}>
        {renderBody()}
      </DialogContent>

      <DialogActions sx={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', justifyContent: 'space-between' }}>
        <span className="text-sm text-gray-500">{footerHint}</span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" size="sm" className="bg-[#EEF6FF]" onClick={() => void handleClose()} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly ? (
            <Tooltip title={saveDisabledReason || ''}>
              <span>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSave()}
                  disabled={!canSave}
                  leftIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : undefined}
                >
                  {revisionChangeId ? 'Save & Resubmit' : 'Save'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
      </DialogActions>

      {!readOnly ? (
        <ColumnTypeChangeDialog
          change={table.pendingTypeChange}
          onKeep={table.cancelTypeChange}
          onConfirm={table.confirmTypeChange}
        />
      ) : null}

      {table.undoToast ? (
        <UndoToast
          key={table.undoToast.id}
          variant="warning"
          position="bottom-right"
          title={table.undoToast.title}
          message={table.undoToast.message}
          timestampLabel="just now"
          onUndo={table.undo}
          onClose={table.dismissUndo}
        />
      ) : null}
    </Dialog>
  )
}

export default AppendixEditorModal
